import {
  StructuredLogger,
  reportContractMismatch,
} from '@brighthub/structured-logging';
import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import {
  ApiService,
  GenericResponseCode,
  GenericResponseResult,
} from '@brighthub/api';
import type { AccountDetailPayload } from '../account-detail.model';
import type { AccountPathType } from '../account-path-type';
import type { AccountsApi } from '../accounts-api.token';
import type {
  AccountTransactionsPayload,
  GetAccountTransactionsOptions,
} from '../account-transactions.model';
import { TRANSACTION_FETCH_LIMIT } from '../account-transactions.model';
import { ACCOUNTS_API_CONFIG } from '../config/accounts-api.config';
import type { GetAccountsPayload } from '../accounts.model';
import {
  isAccountDetailPayload,
  normalizeAccountDetailPayload,
} from './account-detail-response.guard';
import {
  isAccountTransactionsPayload,
  normalizeAccountTransactionsPayload,
} from './account-transactions-response.guard';
import {
  isGetAccountsPayload,
  normalizeAccountsPayload,
} from './accounts-response.guard';

type Envelope<T> = GenericResponseResult & { payload?: T };

/**
 * Live AIM accounts inventory via `api-internal`.
 * Unwraps `{ result, payload }`. Partial `payload.errors` is success (HTTP 200).
 */
@Injectable()
export class HttpAccountsApiService implements AccountsApi {
  private readonly _api = inject(ApiService);
  private readonly _logger = inject(StructuredLogger);
  private readonly _apiName = ACCOUNTS_API_CONFIG.apiName;
  private readonly _pathPrefix = ACCOUNTS_API_CONFIG.pathPrefix;

  getAccounts(guid: string): Observable<GetAccountsPayload> {
    const path = `${this._pathPrefix}?guid=${encodeURIComponent(guid)}`;
    return this._api.get<Envelope<unknown>>(this._apiName, path).pipe(
      switchMap((res) => {
        if (res?.result?.status === 404) {
          return of({ accounts: [] });
        }
        this._assertOk(res);
        const payload = res.payload;
        if (!isGetAccountsPayload(payload)) {
          reportContractMismatch(this._logger, 'accounts.inventory');
          return throwError(
            () => new Error('Accounts GET response failed type-guard'),
          );
        }
        return of(normalizeAccountsPayload(payload));
      }),
      catchError((error: unknown) =>
        error instanceof HttpErrorResponse && error.status === 404
          ? of({ accounts: [] })
          : throwError(() => error),
      ),
    );
  }

  getAccount(
    guid: string,
    pathType: AccountPathType,
    id: string,
  ): Observable<AccountDetailPayload> {
    const path = `${this._pathPrefix}/${encodeURIComponent(pathType)}/${encodeURIComponent(id)}?guid=${encodeURIComponent(guid)}`;
    return this._api.get<Envelope<unknown>>(this._apiName, path).pipe(
      switchMap((res) => {
        this._assertOk(res);
        const payload = res.payload;
        if (!isAccountDetailPayload(payload)) {
          reportContractMismatch(this._logger, 'accounts.detail');
          return throwError(
            () => new Error('Account detail GET response failed type-guard'),
          );
        }
        return of(normalizeAccountDetailPayload(payload));
      }),
    );
  }

  getAccountTransactions(
    guid: string,
    pathType: AccountPathType,
    id: string,
    options?: GetAccountTransactionsOptions,
  ): Observable<AccountTransactionsPayload> {
    const params = new URLSearchParams({
      guid,
      offset: String(options?.offset ?? 0),
      limit: String(options?.limit ?? TRANSACTION_FETCH_LIMIT),
    });
    if (
      options?.lastTransactionId !== undefined &&
      options.lastTransactionId !== null &&
      String(options.lastTransactionId) !== ''
    ) {
      params.set('lastTransactionId', String(options.lastTransactionId));
    }
    if (options?.fromDate) {
      params.set('fromDate', options.fromDate);
    }
    if (options?.toDate) {
      params.set('toDate', options.toDate);
    }
    if (options?.status) {
      params.set('status', options.status);
    }
    const path = `${this._pathPrefix}/${encodeURIComponent(pathType)}/${encodeURIComponent(id)}/transactions?${params.toString()}`;
    return this._api.get<Envelope<unknown>>(this._apiName, path).pipe(
      switchMap((res) => {
        this._assertOk(res);
        const payload = res.payload;
        if (!isAccountTransactionsPayload(payload)) {
          reportContractMismatch(this._logger, 'accounts.transactions');
          return throwError(
            () =>
              new Error('Account transactions GET response failed type-guard'),
          );
        }
        return of(normalizeAccountTransactionsPayload(payload));
      }),
    );
  }

  private _assertOk(res: Envelope<unknown> | null | undefined): void {
    if (!res?.result) {
      throw new Error('Accounts API returned an empty response');
    }
    if (
      res.result.code !== GenericResponseCode.SERVICE_OK ||
      res.result.status !== 200
    ) {
      throw new Error(
        res.result.message?.trim() || `Accounts API error: ${res.result.code}`,
      );
    }
  }
}
